import { useState, useMemo } from "react"
import {Calendar, ChevronDown, Baby, Clock, Activity, Bookmark, RotateCcw} from 'lucide-react'

interface BreedPreset {
  name: string
  days: number
  description: string
}

const BREED_PRESETS: BreedPreset[] = [
  { name: "Holstein Cow", days: 279, description: "Average 279 days gestation" },
  { name: "Jersey Cow", days: 279, description: "Average 279 days gestation" },
  { name: "Highland Cow", days: 290, description: "Average 290 days gestation" },
  { name: "Beef Cattle (General)", days: 283, description: "Average 283 days gestation" },
  { name: "Angus", days: 281, description: "Average 281 days gestation" },
  { name: "Hereford", days: 285, description: "Average 285 days gestation" },
  { name: "Simmental", days: 287, description: "Average 287 days gestation" },
  { name: "Charolais", days: 289, description: "Average 289 days gestation" },
  { name: "Brahman", days: 292, description: "Average 292 days gestation" },
  { name: "Guernsey", days: 283, description: "Average 283 days gestation" },
]

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

function getTrimester(daysPassed: number, totalDays: number): { label: string; number: number; color: string } {
  const third = totalDays / 3
  if (daysPassed <= third) return { label: "First Trimester", number: 1, color: "#40916C" }
  if (daysPassed <= third * 2) return { label: "Second Trimester", number: 2, color: "#D4A373" }
  return { label: "Third Trimester", number: 3, color: "#E76F51" }
}

export default function GestationCalculator() {
  const [breedingDate, setBreedingDate] = useState("")
  const [selectedBreed, setSelectedBreed] = useState(BREED_PRESETS[3]) // Default: Beef Cattle
  const [showResults, setShowResults] = useState(false)

  const results = useMemo(() => {
    if (!breedingDate) return null

    const breeding = new Date(breedingDate + "T00:00:00")
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const calvingDate = new Date(breeding)
    calvingDate.setDate(calvingDate.getDate() + selectedBreed.days)

    const daysPassed = Math.floor((today.getTime() - breeding.getTime()) / (1000 * 60 * 60 * 24))
    const daysRemaining = Math.floor((calvingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    const progress = Math.min(Math.max((daysPassed / selectedBreed.days) * 100, 0), 100)
    const trimester = getTrimester(daysPassed, selectedBreed.days)

    return {
      calvingDate,
      daysPassed: Math.max(daysPassed, 0),
      daysRemaining: Math.max(daysRemaining, 0),
      progress,
      trimester,
      isOverdue: daysRemaining < 0,
      isPast: daysPassed < 0,
    }
  }, [breedingDate, selectedBreed])

  const handleCalculate = () => {
    if (breedingDate) {
      setShowResults(true)
    }
  }

  const handleReset = () => {
    setBreedingDate("")
    setShowResults(false)
  }

  return (
    <section id="gestation-calculator" className="py-12 sm:py-16 lg:py-20 bg-[#FEFAE0]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block text-[#40916C] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Cow Due Date Calculator
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#283618]">
            Cattle Gestation Calculator
          </h2>
          <p className="mt-3 text-[#606C38] text-base sm:text-lg max-w-xl mx-auto">
            Select your cow&apos;s breeding date and breed to calculate the expected calving date, trimester, and days remaining.
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-[#2D6A4F]/8 border border-[#B7E4C7]/40 overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-[#2D6A4F] to-[#40916C] px-6 sm:px-8 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#FEFAE0]" />
              </div>
              <div>
                <h3 className="text-[#FEFAE0] font-bold text-lg" style={{ fontFamily: "'Merriweather', serif" }}>
                  Cow Pregnancy Calculator
                </h3>
                <p className="text-[#B7E4C7] text-sm">
                  Accurate calving date prediction for all major breeds
                </p>
              </div>
            </div>
          </div>

          {/* Input Section */}
          <div className="p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              {/* Breeding Date */}
              <div>
                <label className="block text-sm font-semibold text-[#283618] mb-2">
                  Breeding Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={breedingDate}
                    onChange={(e) => {
                      setBreedingDate(e.target.value)
                      setShowResults(false)
                    }}
                    className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all cursor-pointer"
                  />
                </div>
                <p className="mt-1.5 text-xs text-[#606C38]">
                  Select the date your cow was bred
                </p>
              </div>

              {/* Breed Selection */}
              <div>
                <label className="block text-sm font-semibold text-[#283618] mb-2">
                  Breed Preset
                </label>
                <div className="relative">
                  <select
                    value={selectedBreed.name}
                    onChange={(e) => {
                      const breed = BREED_PRESETS.find((b) => b.name === e.target.value)
                      if (breed) {
                        setSelectedBreed(breed)
                        setShowResults(false)
                      }
                    }}
                    className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all appearance-none cursor-pointer pr-10"
                  >
                    {BREED_PRESETS.map((breed) => (
                      <option key={breed.name} value={breed.name}>
                        {breed.name} ({breed.days} days)
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#606C38] pointer-events-none" />
                </div>
                <p className="mt-1.5 text-xs text-[#606C38]">
                  Gestation period: <strong>{selectedBreed.days} days</strong>
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCalculate}
                disabled={!breedingDate}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2D6A4F] hover:bg-[#245a42] disabled:bg-[#B7E4C7] disabled:cursor-not-allowed text-[#FEFAE0] font-bold text-base sm:text-lg px-6 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:shadow-none disabled:translate-y-0"
              >
                <Baby className="w-5 h-5" />
                Calculate Calving Date
              </button>
              {showResults && (
                <button
                  onClick={handleReset}
                  className="inline-flex items-center justify-center gap-2 bg-[#FEFAE0] hover:bg-[#f0ead2] text-[#606C38] font-semibold px-5 py-4 rounded-xl border-2 border-[#B7E4C7] transition-all duration-200"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Results Section */}
          {showResults && results && (
            <div className="border-t-2 border-[#B7E4C7]/40 bg-gradient-to-b from-[#FEFAE0]/50 to-white p-6 sm:p-8 animate-fade-in-up">
              {/* Main Result */}
              <div className="text-center mb-6 pb-6 border-b border-[#B7E4C7]/30">
                <p className="text-sm font-semibold text-[#40916C] uppercase tracking-wider mb-1">
                  Expected Calving Date
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-[#283618]" style={{ fontFamily: "'Merriweather', serif" }}>
                  {formatDate(results.calvingDate)}
                </p>
                {results.isOverdue && (
                  <p className="mt-2 text-sm font-semibold text-[#E76F51]">
                    This cow may have already calved or is overdue.
                  </p>
                )}
                {results.isPast && (
                  <p className="mt-2 text-sm font-semibold text-[#606C38]">
                    The breeding date is in the future.
                  </p>
                )}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Trimester */}
                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4" style={{ color: results.trimester.color }} />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Trimester</span>
                  </div>
                  <p className="text-lg font-bold text-[#283618]">{results.trimester.label}</p>
                  <div className="mt-2 flex gap-1">
                    {[1, 2, 3].map((n) => (
                      <div
                        key={n}
                        className="h-1.5 flex-1 rounded-full transition-colors"
                        style={{
                          backgroundColor: n <= results.trimester.number ? results.trimester.color : "#B7E4C7",
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Days Remaining */}
                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-[#D4A373]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Days Remaining</span>
                  </div>
                  <p className="text-3xl font-bold text-[#283618]">{results.daysRemaining}</p>
                  <p className="text-xs text-[#606C38] mt-1">of {selectedBreed.days} total days</p>
                </div>

                {/* Progress */}
                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Baby className="w-4 h-4 text-[#2D6A4F]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Progress</span>
                  </div>
                  <p className="text-3xl font-bold text-[#283618]">{Math.round(results.progress)}%</p>
                  <div className="mt-2 h-2 bg-[#B7E4C7]/40 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${results.progress}%`,
                        backgroundColor: results.trimester.color,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Bookmark CTA */}
              <div className="mt-6 bg-[#2D6A4F]/5 rounded-xl p-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                <Bookmark className="w-5 h-5 text-[#2D6A4F] shrink-0" />
                <p className="text-sm text-[#283618] text-center sm:text-left">
                  <strong>Bookmark this tool</strong> on your home screen for quick access in the field. Track multiple cows throughout the breeding season.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
